import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Lock } from 'lucide-react';
import { JourneyDashboard } from '../components/discovery/JourneyDashboard';
import { DreamJournalPrompt } from '../components/discovery/DreamJournalPrompt';
import { SeasonalPrompts } from '../components/discovery/SeasonalPrompts';
import { useAuth } from '../hooks/useAuth';
import { useCredits } from '../hooks/useCredits';
import { supabase } from '../lib/supabase';
import { useI18n } from '../hooks/useI18n';
import { getSignEmoji, getSignName } from '../lib/astrology';
import { EmptyState } from '../components/ui/EmptyState';
import { Spinner } from '../components/ui/Spinner';
import { DailyAffirmations } from '../components/shared/DailyAffirmations';
import { safeInvoke } from '../lib/safeInvoke';
import { DailyBriefHero } from '../components/dashboard/DailyBriefHero';
import { MainActions } from '../components/dashboard/MainActions';
import { CelestialTimeline } from '../components/dashboard/CelestialTimeline';
import { FreeUserDashboard } from '../components/dashboard/FreeUserDashboard';
import { WelcomeBanner } from '../components/WelcomeBanner';

// ─── New paid user welcome banner ────────────────────────────────────────────
type PlanTier = 'free' | 'monthly' | 'annual';

const ALL_SERVICES: { glyph: string; label: string; to: string; tier: PlanTier }[] = [
  { glyph: '⊙',  label: 'Mapa Natal',           to: '/birth-chart',      tier: 'free'    },
  { glyph: '☾',  label: 'Previsão',              to: '/forecast',         tier: 'monthly' },
  { glyph: '∞',  label: 'Sinastria',             to: '/synastry',         tier: 'monthly' },
  { glyph: '≈',  label: 'AstroMatch',            to: '/astromatch',       tier: 'monthly' },
  { glyph: '≡',  label: 'Tarô',                  to: '/tarot',            tier: 'free'    },
  { glyph: '🔢', label: 'Numerologia',           to: '/numerology',       tier: 'free'    },
  { glyph: '⚡', label: 'Design Humano',         to: '/human-design',     tier: 'monthly' },
  { glyph: '🕉️', label: 'Astrologia Védica',    to: '/vedic-astrology',  tier: 'monthly' },
  { glyph: '☯',  label: 'I Ching',              to: '/i-ching',          tier: 'free'    },
  { glyph: '🤚', label: 'Quiromância',           to: '/quiromancy',       tier: 'monthly' },
  { glyph: '💤', label: 'Sonhos',               to: '/dream-journal',    tier: 'monthly' },
  { glyph: '℞',  label: 'Radar Retrógrado',     to: '/retrograde-radar', tier: 'monthly' },
  { glyph: '🌑', label: 'Eclipse Insight',       to: '/eclipses',         tier: 'monthly' },
  { glyph: '📅', label: 'Agenda Astrológica',   to: '/agenda',           tier: 'monthly' },
  { glyph: '⌘',  label: 'Pergunte aos Astros',  to: '/questions',        tier: 'monthly' },
];

function serviceIsLocked(tier: PlanTier, subscriptionTier: string, isUnlimited: boolean): boolean {
  if (isUnlimited) return false;
  if (tier === 'free') return false;
  if (tier === 'monthly') return subscriptionTier === 'free';
  if (tier === 'annual') return subscriptionTier !== 'annual';
  return false;
}

function NewPaidWelcomeBanner({
  userId,
  subscriptionTier,
  isUnlimited,
}: {
  userId: string;
  subscriptionTier: string;
  isUnlimited: boolean;
}) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const isPaid = subscriptionTier !== 'free' || isUnlimited;
  const storageKey = `professya_welcome_paid_${userId}`;
  const [dismissed, setDismissed] = useState(() => isPaid && localStorage.getItem(storageKey) === '1');
  const [isProfessional, setIsProfessional] = useState(false);

  useEffect(() => {
    if (!userId) return;
    supabase
      .from('astrologer_profiles')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle()
      .then(({ data }) => { if (data) setIsProfessional(true); });
  }, [userId]);

  if (dismissed) return null;

  function dismiss() {
    if (isPaid) {
      localStorage.setItem(storageKey, '1');
      setDismissed(true);
    }
  }

  return (
    <div className="card" style={{
      marginBottom: '1.75rem',
      border: '1px solid rgba(31, 78, 95, 0.25)',
      background: 'var(--bg-surface)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.875rem' }}>
        <div>
          {isPaid ? (
            <>
              <p className="eyebrow" style={{ color: 'var(--petrol)', marginBottom: '0.2rem' }}>
                {t('dashboard.premiumBadge')}
              </p>
              <h2 style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: '1.125rem', fontWeight: 400, color: 'var(--bg)', margin: '0 0 0.2rem' }}>
                {t('dashboard.premiumWelcomeTitle')}
              </h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: 0 }}>
                {t('dashboard.premiumWelcomeDesc')}
              </p>
            </>
          ) : (
            <>
              <h2 style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: '1.125rem', fontWeight: 400, color: 'var(--bg)', margin: '0 0 0.2rem' }}>
                Explore todos os recursos
              </h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: 0 }}>
                Itens bloqueados estão disponíveis nos planos pagos.
              </p>
            </>
          )}
        </div>
        {isPaid && (
          <button className="btn btn-ghost btn-icon" onClick={dismiss} aria-label={t('dashboard.premiumClose')} style={{ flexShrink: 0, marginLeft: '0.5rem' }}>
            <X size={18} />
          </button>
        )}
      </div>

      {/* All services as compact chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
        {ALL_SERVICES.map(s => {
          const locked = serviceIsLocked(s.tier, subscriptionTier, isUnlimited);
          return (
            <button
              key={s.to}
              onClick={() => {
                if (locked) { navigate('/pricing'); return; }
                dismiss();
                navigate(s.to);
              }}
              title={locked ? 'Disponível nos planos pagos' : s.label}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                padding: '0.35rem 0.6rem',
                backgroundColor: locked ? 'transparent' : 'var(--bg-elevated)',
                border: `1px solid ${locked ? 'var(--border-subtle)' : 'var(--line-soft)'}`,
                borderRadius: 'var(--radius-full)',
                cursor: locked ? 'default' : 'pointer',
                color: 'inherit',
                fontSize: '0.72rem',
                whiteSpace: 'nowrap',
                opacity: locked ? 0.45 : 1,
                transition: 'opacity 150ms',
              }}
            >
              <span style={{ fontSize: '0.85rem' }}>{s.glyph}</span>
              <span style={{ color: locked ? 'var(--text-muted)' : 'var(--celeste-soft)', lineHeight: 1.2 }}>{s.label}</span>
              {locked && <Lock size={10} style={{ flexShrink: 0 }} />}
            </button>
          );
        })}

        {/* Professional module badge, only shown if the user has an astrologer profile */}
        {isProfessional && (
          <button
            onClick={() => { dismiss(); navigate('/profissional'); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
              padding: '0.35rem 0.6rem',
              backgroundColor: 'rgba(245,200,66,0.1)',
              border: '1px solid rgba(245,200,66,0.35)',
              borderRadius: 'var(--radius-full)',
              cursor: 'pointer',
              color: 'inherit',
              fontSize: '0.72rem',
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ fontSize: '0.85rem' }}>✦</span>
            <span style={{ color: 'var(--petrol)', lineHeight: 1.2, fontWeight: 600 }}>Módulo Profissional</span>
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Subscription renewal warning ────────────────────────────────────────────
function RenewalWarningBanner({ expiresAt }: { expiresAt: string }) {
  const daysLeft = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  const navigate  = useNavigate();
  const { t } = useI18n();

  if (daysLeft > 7 || daysLeft < 0) return null;

  const when = daysLeft === 0
    ? t('dashboard.renewalToday')
    : daysLeft === 1
    ? t('dashboard.renewalTomorrow')
    : t('dashboard.renewalDays', { days: String(daysLeft) });

  return (
    <div style={{
      marginBottom: '1.5rem',
      padding: '0.875rem 1rem',
      borderRadius: 'var(--radius-card)',
      border: '1px solid rgba(251,146,60,0.4)',
      backgroundColor: 'rgba(251,146,60,0.08)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '0.75rem',
      flexWrap: 'wrap',
    }}>
      <div>
        <p style={{ fontWeight: 600, color: '#fb923c', margin: '0 0 0.15rem', fontSize: 'var(--text-label)' }}>
          {t('dashboard.renewalTitle', { when })}
        </p>
        <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: 'var(--text-caption)' }}>
          {t('dashboard.renewalDesc')}
        </p>
      </div>
      <button className="btn btn-sm" onClick={() => navigate('/pricing')}
        style={{ background: '#fb923c', color: '#0a0d1a', fontWeight: 600, border: 'none', flexShrink: 0 }}>
        {t('dashboard.renewalCta')}
      </button>
    </div>
  );
}

// ─── Streak widget ───────────────────────────────────────────────────────────
function StreakWidget({ userId }: { userId: string }) {
  const { t }             = useI18n();
  const [streak, setStreak] = useState<number | null>(null);

  useEffect(() => {
    if (!userId) return;
    supabase
      .from('user_streaks')
      .select('current_streak')
      .eq('user_id', userId)
      .maybeSingle()
      .then(({ data }) => {
        if (data && data.current_streak > 0) setStreak(data.current_streak);
      });
  }, [userId]);

  if (!streak) return null;

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
      padding: '0.35rem 0.875rem',
      backgroundColor: 'var(--bg-elevated)',
      border: '1px solid var(--line-soft)',
      borderRadius: 'var(--radius-full)',
      fontSize: 'var(--text-13)',
      color: 'var(--petrol)',
      marginTop: '0.5rem',
      fontFamily: 'var(--font-body)',
    }}>
      ✦ {streak} {streak === 1 ? t('dashboard.streakSingular') : t('dashboard.streakPlural')}
    </div>
  );
}


// ─── Profile banner (no birth data) ─────────────────────────────────────────
function NoBirthDataBanner() {
  const { t }    = useI18n();
  const navigate = useNavigate();
  return (
    <div
      className="card"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '1rem',
        marginBottom: '2rem',
        border: '1px solid rgba(31, 78, 95, 0.25)',
        backgroundColor: 'var(--bg-surface)',
      }}
    >
      <div>
        <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.25rem', fontSize: 'var(--text-label)' }}>
          {t('dashboard.completeBanner')}
        </p>
        <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: 'var(--text-caption)' }}>
          {t('dashboard.completeBannerSub')}
        </p>
      </div>
      <button className="btn btn-primary" onClick={() => navigate('/profile')}>
        {t('dashboard.addData')}
      </button>
    </div>
  );
}

// ─── "Continue journey", recent readings placeholder ────────────────────────
function JourneySection({ hasBirthData }: { hasBirthData: boolean }) {
  const { t }    = useI18n();
  const navigate = useNavigate();

  const SUGGESTIONS = [
    { glyph: '⊙', titleKey: 'nav.birthChart',             descKey: 'dashboard.suggBirthChartDesc', to: '/birth-chart', requiresBirth: true },
    { glyph: '☾', titleKey: 'dashboard.suggForecastTitle', descKey: 'dashboard.suggForecastDesc',  to: '/forecast',    requiresBirth: true },
    { glyph: '⌘', titleKey: 'nav.questions',               descKey: 'dashboard.suggQuestionsDesc', to: '/questions',   requiresBirth: false },
  ].filter(s => !s.requiresBirth || hasBirthData);

  if (SUGGESTIONS.length === 0) return null;

  return (
    <section>
      <div style={{ marginBottom: '1rem' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 400, fontSize: 'var(--text-22)', color: 'var(--bg)', margin: 0 }}>
          {t('dashboard.continueJourney')}
        </h2>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {SUGGESTIONS.map(({ glyph, titleKey, descKey, to }) => (
          <div
            key={to}
            className="card"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              cursor: 'pointer',
              transition: 'border-color var(--transition-fast)',
            }}
            onClick={() => navigate(to)}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = 'var(--line-medium)'}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = 'var(--line-soft)'}
            role="link"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && navigate(to)}
          >
            <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.75rem', color: 'var(--petrol)', flexShrink: 0, width: '2rem', textAlign: 'center' }}>{glyph}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 400, color: 'var(--bg)', margin: '0 0 0.15rem', fontSize: 'var(--text-18)' }}>
                {t(titleKey)}
              </p>
              <p style={{ color: 'var(--celeste-soft)', margin: 0, fontSize: 'var(--text-13)', fontFamily: 'var(--font-body)' }}>
                {t(descKey)}
              </p>
            </div>
            <span style={{ color: 'var(--petrol)', fontFamily: 'var(--font-display)', fontSize: 'var(--text-18)', flexShrink: 0 }}>→</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Main Dashboard page ─────────────────────────────────────────────────────
export function DashboardPage() {
  const { profile, loading, refreshProfile } = useAuth();
  const { credits }                          = useCredits();
  const { t }                = useI18n();
  const navigate             = useNavigate();
  const [forecastText, setForecastText] = useState('');
  const [forecastLoading, setForecastLoading] = useState(true);

  // Refresh profile on every dashboard visit to ensure data is current
  useEffect(() => { refreshProfile(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setForecastLoading(true);
    const today = new Date().toISOString().slice(0, 10);

    // Check personal_forecasts directly, skips the edge function entirely on cache hits.
    supabase
      .from('personal_forecasts')
      .select('content')
      .eq('forecast_type', 'daily_personal')
      .eq('period_key', today)
      .gt('expires_at', new Date().toISOString())
      .maybeSingle()
      .then(({ data: cached }) => {
        const cachedText = (cached?.content as Record<string, string> | null)?.text ?? '';
        if (cachedText) {
          setForecastText(cachedText);
          setForecastLoading(false);
          return;
        }
        // No DB cache, invoke edge function (which will generate and store the result)
        safeInvoke<{ text?: string; forecast?: string }>('forecast', {
          body: { period: 'daily' },
          fallback: {},
          timeout: 35_000,
        }).then(({ data }) => {
          setForecastText(data?.text ?? data?.forecast ?? '');
          setForecastLoading(false);
        });
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="page-container" style={{ display: 'flex', justifyContent: 'center', paddingTop: '5rem' }}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="page-container">
        <EmptyState
          emoji="🌌"
          title={t('dashboard.errorTitle')}
          subtitle={t('dashboard.errorSubtitle')}
          cta={t('dashboard.reload')}
          ctaAction={() => window.location.reload()}
        />
      </div>
    );
  }

  const firstName  = profile.full_name?.split(' ')[0] ?? 'você';
  const signEmoji  = profile.sun_sign ? getSignEmoji(profile.sun_sign) : '';
  const signName   = profile.sun_sign ? getSignName(profile.sun_sign) : '';
  const hasBirth   = Boolean(profile.birth_date);
  const isFree     = profile.subscription_tier === 'free' && !profile.unlimited_access;

  // Greeting based on time of day
  const hour   = new Date().getHours();
  const greeting =
    hour < 12 ? t('dashboard.greetingMorning') :
    hour < 18 ? t('dashboard.greetingAfternoon') :
                t('dashboard.greetingEvening');

  return (
    <div className="page-container">

      {/* Header */}
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 400, fontSize: 'clamp(1.5rem, 4vw, 2.25rem)', color: 'var(--bg)', letterSpacing: '-0.02em', marginBottom: '0.25rem' }}>
          {greeting}, {firstName} ✦
        </h1>
        {signEmoji && signName && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span className="sign-badge">
              {signEmoji} {signName}
            </span>
            {(profile.subscription_tier !== 'free' || profile.unlimited_access === true) && (
              <span className="badge badge-brass">{t('dashboard.tierPremium')}</span>
            )}
          </div>
        )}
        {/* Streak */}
        <StreakWidget userId={profile.id} />

        {/* Credit pills */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
          {credits.questions > 0 && (
            <button className="credit-pill" onClick={() => navigate('/questions')} style={{ cursor: 'pointer', background: 'none', border: 'none' }}>
              ⌘ {credits.questions} {credits.questions !== 1 ? t('dashboard.creditQuestionsPlural') : t('dashboard.creditQuestionsSingular')}
            </button>
          )}
          {credits.synastry > 0 && (
            <span className="credit-pill">∞ {credits.synastry} {t('dashboard.creditSynastry')}</span>
          )}
          {credits.astromatch > 0 && (
            <span className="credit-pill">≈ {credits.astromatch} AstroMatch</span>
          )}
        </div>
      </div>

      {/* Daily brief hero, above the fold */}
      <DailyBriefHero forecastText={forecastText} loading={forecastLoading} />

      {/* Single top-priority banner (WelcomeFree > NoBirthData > Renewal > ServicesGrid) */}
      {isFree && !hasBirth ? (
        <WelcomeBanner userId={profile.id} userName={profile.full_name ?? ''} />
      ) : !hasBirth ? (
        <NoBirthDataBanner />
      ) : profile.subscription_expires_at ? (
        <RenewalWarningBanner expiresAt={profile.subscription_expires_at} />
      ) : (
        <NewPaidWelcomeBanner
          userId={profile.id}
          subscriptionTier={profile.subscription_tier ?? 'free'}
          isUnlimited={profile.unlimited_access === true}
        />
      )}

      {/* 3 main action cards */}
      <MainActions />

      {/* 7-day celestial timeline */}
      <CelestialTimeline />

      {/* Daily affirmations, collapsible, shown when birth data exists */}
      {hasBirth && <DailyAffirmations userId={profile.id} collapsible />}

      {/* Free user: soft paywall with planet interpretations */}
      {isFree && hasBirth && (
        <div style={{ marginTop: '1.75rem' }}>
          <FreeUserDashboard userId={profile.id} />
        </div>
      )}

      {/* Continue journey */}
      <JourneySection hasBirthData={hasBirth} />

      {/* Contextual discovery prompts */}
      <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <DreamJournalPrompt />
        <SeasonalPrompts />
      </div>

      {/* Feature discovery, shown after first session */}
      <div style={{ marginTop: '1.75rem' }}>
        <JourneyDashboard />
      </div>

    </div>
  );
}
